const BASE_URL = process.env.REACT_APP_API_URL ?? window.location.origin;

export type ChatPart =
  | { text: string }
  | { functionCall: { name: string; args: any } }
  | { functionResponse: { name: string; response: any } };

export type ChatMessage = {
  role: "user" | "model";
  parts: ChatPart[];
};

export type ChatResponse = {
  reply: string;
  history: ChatMessage[];
};

// ── Proposed changes (agent write tools) ─────────────────
// A reply can propose changes (new season, calibration edit, …). Nothing is
// saved until the user confirms one: resolveAgentAction.

export type AgentActionChange = { key: string; from: unknown; to: unknown };

export type AgentAction = {
  id: string;
  tool: string;
  preview: { tool: string; target: string; changes: AgentActionChange[] };
  status: "pending" | "done" | "cancelled" | "failed";
  error?: string;
};

// ── Streaming chat (server-owned history) ─────────────────
// POST /api/v1/agent/chat/stream emits SSE events:
//   meta {conversationId} → tool {name,label} / tool_done {name} /
//   text {delta} → done {conversationId,title,reply,actions} | error {message}

export type StreamCallbacks = {
  onMeta?: (data: { conversationId: string }) => void;
  onText?: (delta: string) => void;
  onTool?: (data: { name: string; label: string }) => void;
  onToolDone?: (data: { name: string }) => void;
  onDone?: (data: {
    conversationId: string;
    title?: string;
    reply: string;
    actions?: AgentAction[];
  }) => void;
  onError?: (message: string) => void;
};

export const streamChatMessage = async (
  params: {
    message: string;
    conversationId?: string;
    language: string;
    signal?: AbortSignal;
  },
  callbacks: StreamCallbacks
): Promise<void> => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const response = await fetch(`${BASE_URL}/api/v1/agent/chat/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    signal: params.signal,
    body: JSON.stringify({
      message: params.message,
      conversationId: params.conversationId,
      language: params.language,
      timezone,
    }),
  });

  if (!response.ok || !response.body) {
    const data = await response.json().catch(() => null);
    throw new Error(data?.error ?? data?.message ?? "Failed to send message");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let eventName = "message";
  let dataLines: string[] = [];

  const dispatch = () => {
    if (dataLines.length === 0) return;
    const raw = dataLines.join("\n");
    dataLines = [];
    const name = eventName;
    eventName = "message";
    let data: any;
    try {
      data = JSON.parse(raw);
    } catch {
      return;
    }
    switch (name) {
      case "meta":
        callbacks.onMeta?.(data);
        break;
      case "text":
        callbacks.onText?.(data.delta ?? "");
        break;
      case "tool":
        callbacks.onTool?.(data);
        break;
      case "tool_done":
        callbacks.onToolDone?.(data);
        break;
      case "done":
        callbacks.onDone?.(data);
        break;
      case "error":
        callbacks.onError?.(data.message ?? "Something went wrong");
        break;
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf("\n")) !== -1) {
      const line = buffer.slice(0, idx).replace(/\r$/, "");
      buffer = buffer.slice(idx + 1);
      if (line === "") {
        dispatch();
      } else if (line.startsWith("event:")) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice(5).replace(/^ /, ""));
      }
      // id:/retry:/comment lines are ignored
    }
  }
  dispatch();
};

// ── Feedback ──────────────────────────────────────────────

export const sendFeedback = async (
  conversationId: string,
  messageId: number,
  rating: "up" | "down" | null
): Promise<void> => {
  await fetch(`${BASE_URL}/api/v1/agent/feedback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ conversationId, messageId, rating }),
  }).catch(() => undefined);
};

/**
 * Confirm (approve) or cancel a proposed change. Answers the resolved action
 * and the conversation's messages; a failed save comes back as status
 * "failed" with an error. Throws with `status` 409 when the change was already
 * handled or the conversation changed meanwhile (reload it).
 */
export const resolveAgentAction = async (
  conversationId: string,
  actionId: string,
  approve: boolean
): Promise<{ action: AgentAction; messages: any[] }> => {
  const res = await fetch(`${BASE_URL}/api/v1/agent/actions/${encodeURIComponent(actionId)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ conversationId, approve }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(data?.error ?? "Failed to save the change") as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return data;
};

// ── Legacy non-streaming chat (kept as fallback) ──────────

export const sendChatMessage = async (
  message: string,
  history: ChatMessage[],
  language: string
): Promise<ChatResponse> => {
  const url = `${BASE_URL}/api/v1/agent/chat`;
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ message, history, language }),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    // Backend 500 responses still carry { reply, history } — return them
    if (data?.reply) {
      return data as ChatResponse;
    }
    throw new Error(data?.error ?? data?.message ?? "Failed to send message");
  }
  return data as ChatResponse;
};

// ── Conversation persistence API ─────────────────────────

export type ConversationMeta = {
  id: string;
  title: string;
  messages: any[];
  createdAt: number;
  updatedAt: number;
};

export type ConversationFull = ConversationMeta & {
  history: ChatMessage[];
};

export const listConversations = async (): Promise<ConversationMeta[]> => {
  const url = `${BASE_URL}/api/v1/conversations`;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) return [];
  return res.json();
};

export const getConversation = async (id: string): Promise<ConversationFull | null> => {
  const url = `${BASE_URL}/api/v1/conversations/${id}`;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) return null;
  return res.json();
};

export const updateConversation = async (
  id: string,
  data: { title?: string; messages?: any[]; history?: any[] }
): Promise<void> => {
  const url = `${BASE_URL}/api/v1/conversations/${id}`;
  await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });
};

export const deleteConversation = async (id: string): Promise<void> => {
  const url = `${BASE_URL}/api/v1/conversations/${id}`;
  await fetch(url, { method: "DELETE", credentials: "include" });
};
